import React, { useState, useEffect, useRef, useCallback } from "react";
import * as Sentry from "@sentry/react";
import { supabase } from "./supabase";
import { useCapabilities, pagesAllowedByCaps } from "./utils/capabilities";
import { fetchMyTenant } from "./utils/tenantArchive";
import { Input, Btn, TextLink, CompanyScope, SearchTrigger, MenuItem } from "./ui";
import { safeNum, parseLocalDate, formatLocalDate, shortId, CLASS_COLORS, ALLOWED_DOC_TYPES, ALLOWED_DOC_EXTENSIONS, pickColor, generateId, formatPersonName, buildNameFields, parseNameParts, isValidEmail, normalizeEmail, formatCurrency, formatPhoneInput, sanitizeFileName, exportToCSV, buildAddress, escapeHtml, escapeFilterValue, sanitizeForPrint, US_STATES, STATE_NAMES, statusColors, priorityColors, emailFilterValue, getWizardApplicableSteps, canReviewRequest, fmtDate, daysUntil } from "./utils/helpers";
import { PM_ERRORS, pmError, reportError, logErrorToSupabase, detectInfrastructureCode, setShowToastGlobal, setActiveErrorContext } from "./utils/errors";
import { guardSubmit, guardRelease, guarded, requireCompanyId } from "./utils/guards";
import { encryptCredential, decryptCredential } from "./utils/encryption";
import { AUDIT_ACTIONS, AUDIT_MODULES, logAudit } from "./utils/audit";
import { pageUrl, sweptUrl, pageForPath, normalizeLegacyUrl } from "./utils/routes";
import { queueNotification } from "./utils/notifications";
import { companyQuery, companyInsert, companyUpsert, checkRPCHealth, runDataIntegrityChecks, loadCompanySettings, clearMembershipCache } from "./utils/company";
import { COMPANY_DEFAULTS } from "./config";
import { safeLedgerInsert, atomicPostJEAndLedger, postAccountingTransaction, checkPeriodLock, autoPostJournalEntry, checkAccrualExists, autoOwnerDistribution, getPropertyClassId, resolveAccountId, getOrCreateTenantAR, fetchAllPaged,  ensureDefaultAccounts, _classIdCache, _acctIdCache, _acctCodeToName, _tenantArCache, _zipCache, lookupZip } from "./utils/accounting";
import { ErrorBoundary, Badge, StatCard, Spinner, Modal, ToastContainer, ConfirmModal, PropertyDropdown, TenantSelect, PropertySelect, DocUploadModal, formatAllTenants, generatePaymentReceipt } from "./components/shared";
import PullToRefresh from "./components/PullToRefresh";
import FileViewer from "./components/FileViewer";

import { LandingPage } from "./components/LandingPage";
import { LoginPage } from "./components/LoginPage";
import { MfaGate, StepUpHost } from "./components/SecondStep";
import { mfaState } from "./utils/mfa";
import { Dashboard } from "./components/Dashboard";
import { loadOpenWork } from "./utils/openWork";
import { RoleManagement, AuditTrail, ArchivePage, ArchivedItems, ErrorLogDashboard, TasksAndApprovals, UserProfile, AdminPage } from "./components/Admin";
import { CompanySelector, PendingRequestsPanel, PendingPMAssignments } from "./components/CompanySelector";
import { InvitationBanner, InviteLinkModal, useMyInvitations, answerInvitation, INVITE_ROLE_LABELS } from "./components/Invitations";
import { captureInviteToken } from "./utils/invitations";
import { CommandPalette } from "./components/CommandPalette";
import { ShortcutsHelp, useShortcutsHost, MOD } from "./components/KeyboardShortcuts";
import { HOUSY } from "./utils/housy";
import { lazyPage } from "./utils/lazyPage";
import { BrandMark } from "./components/BrandMark";
// Pages load when first opened, not all in one 5.8 MB bundle (audit OPS-14).
const Properties = lazyPage(() => import("./components/Properties"), "Properties");
const Tenants = lazyPage(() => import("./components/Tenants"), "default");
const Prospects = lazyPage(() => import("./components/Prospects"), "Prospects");
const Payments = lazyPage(() => import("./components/Payments"), "Payments");
const Maintenance = lazyPage(() => import("./components/Maintenance"), "Maintenance"), Inspections = lazyPage(() => import("./components/Maintenance"), "Inspections"), VendorManagement = lazyPage(() => import("./components/Maintenance"), "VendorManagement");
const Utilities = lazyPage(() => import("./components/Utilities"), "Utilities");
const Accounting = lazyPage(() => import("./components/Accounting"), "Accounting");
const Documents = lazyPage(() => import("./components/Documents"), "Documents"), DocumentBuilder = lazyPage(() => import("./components/Documents"), "DocumentBuilder");
const Housy = lazyPage(() => import("./components/Housy"), "Housy");
const LeaseManagement = lazyPage(() => import("./components/Leases"), "LeaseManagement");
const OwnerManagement = lazyPage(() => import("./components/Owners"), "OwnerManagement"), OwnerPortal = lazyPage(() => import("./components/Owners"), "OwnerPortal");
const TenantPortal = lazyPage(() => import("./components/TenantPortal"), "TenantPortal");
const MoveOutWizard = lazyPage(() => import("./components/Lifecycle"), "MoveOutWizard"), EvictionWorkflow = lazyPage(() => import("./components/Lifecycle"), "EvictionWorkflow");
const EmailNotifications = lazyPage(() => import("./components/Notifications"), "EmailNotifications");
const Messages = lazyPage(() => import("./components/Messages"), "Messages");
const PropertyImport = lazyPage(() => import("./components/PropertyImport"), "default");
const HOAPayments = lazyPage(() => import("./components/HOA"), "HOAPayments");
const Loans = lazyPage(() => import("./components/Loans"), "Loans");
const InsuranceTracker = lazyPage(() => import("./components/Insurance"), "InsuranceTracker");
const TaxBills = lazyPage(() => import("./components/TaxBills"), "TaxBills");
const Licenses = lazyPage(() => import("./components/Licenses"), "Licenses");
const LateFees = lazyPage(() => import("./components/LateFees"), "LateFees");
const PublicSignPage = lazyPage(() => import("./components/PublicSignPage"), "default");
const PublicApplyPage = lazyPage(() => import("./components/PublicApplyPage"), "default");

// ============ SENTRY INITIALIZATION ============
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN || "",
  // Which deploy and which site an error came from (OPS-23).
  environment: process.env.REACT_APP_VERCEL_ENV || process.env.NODE_ENV || "development",
  release: process.env.REACT_APP_VERCEL_GIT_COMMIT_SHA || undefined,
  enabled: process.env.NODE_ENV === "production" && !!process.env.REACT_APP_SENTRY_DSN,
  sampleRate: 1.0,
  tracesSampleRate: 0,
  beforeSend(event) {
    const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+/g;
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map(b => ({
        ...b,
        message: b.message?.replace(EMAIL_RE, "[email]")
      }));
    }
    // A Postgres error text can quote the row it refused, email and all.
    if (typeof event.message === "string") event.message = event.message.replace(EMAIL_RE, "[email]");
    if (event.extra && typeof event.extra.rawMessage === "string") event.extra.rawMessage = event.extra.rawMessage.replace(EMAIL_RE, "[email]");
    // Drop errors whose entire stack is browser-extension or anonymous
    // eval'd code. Grammarly / 1Password / LastPass / ad blockers / AI
    // sidebars inject scripts into every page; our CSP correctly
    // refuses their eval calls, and the resulting EvalError surfaces
    // here even though nothing in our bundle is at fault. Keep the
    // event only if at least one frame has a filename rooted in our
    // own code.
    const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
    if (frames.length > 0) {
      const isOurFrame = (f) => {
        const file = f.filename || "";
        if (!file || file === "<anonymous>") return false;
        if (file.startsWith("chrome-extension://")) return false;
        if (file.startsWith("moz-extension://")) return false;
        if (file.startsWith("safari-extension://")) return false;
        if (file.startsWith("safari-web-extension://")) return false;
        return true;
      };
      if (!frames.some(isOurFrame)) return null;
    }
    return event;
  },
  ignoreErrors: [
    "ResizeObserver loop",
    "Non-Error promise rejection",
    "Load failed",
    "ChunkLoadError",
    // CSP-blocked eval from extensions (see beforeSend); regex catches
    // the variant where the stack arrives unparseable.
    /Refused to evaluate a string as JavaScript/i,
    // Benign Web Locks noise from @supabase/auth-js. When the auth
    // token-refresh lock is held too long (a backgrounded/suspended tab,
    // device sleep, or a second tab), auth-js intentionally steals the lock
    // as a recovery mechanism (locks.js, { steal: true }). The previous
    // holder's navigator.locks promise then rejects with this DOMException.
    // Auth keeps working — the steal IS the recovery — so it's pure noise.
    // Dot matches straight/curly quote variants.
    /Lock broken by another request with the .steal. option/i,
  ],
});
window.Sentry = Sentry;


// Global errors: Sentry's own handlers capture them. This only keeps a
// console line for local work. (It used to POST them to audit_trail with the
// anon key, which row security always refused -- nothing was ever logged
// there; audit OPS-11.)
(function initErrorTracking() {
  let n = 0;
  const logError = (source, message, stack) => { if (n++ < 50) console.error(`[ErrorTracking/${source}]`, message, stack || ""); };
  window.onerror = (msg, src, line, col, err) => { logError("window.onerror", `${msg} at ${src}:${line}:${col}`, err?.stack); };
  window.addEventListener("unhandledrejection", (e) => { logError("unhandledrejection", String(e.reason?.message || e.reason || "Unknown promise rejection").slice(0, 500), e.reason?.stack); });
})();

// ============ TOAST NOTIFICATION SYSTEM ============
let _toastIdCounter = 0;

// ============ ROLE DEFINITIONS ============
// Accounting sub-page IDs — match ALL_NAV's accounting.children
// below. Listed once and spread into each role that has accounting
// access so they show up in the expanded sidebar.
const ACCT_SUB_PAGES = ["acct_opening","acct_coa","acct_journal","acct_recurring","acct_bankimport","acct_qbimport","acct_reconcile","acct_classes","acct_reports"];

const ROLES = {
  admin: { label: "Admin", color: "bg-brand-600", pages: ["dashboard","tasks","properties","property_import","property_import_add","property_import_edit","tenants","prospects","payments","maintenance","utilities","hoa","loans","insurance","tax_bills","licenses","accounting",...ACCT_SUB_PAGES,"owners","notifications","messages","admin","documents","doc_builder","leases","inspections","vendors","moveout","evictions","latefees","housy"] },
  // Manager sits between admin and the customizable staff roles. Can
  // review / approve requests submitted by staff who've been explicitly
  // assigned to them via manager_email. Can't administer the company
  // (no members page, no role edits).
  manager: { label: "Manager", color: "bg-brand-400", pages: ["dashboard","tasks","properties","property_import","property_import_add","property_import_edit","tenants","prospects","payments","maintenance","utilities","hoa","tax_bills","licenses","accounting",...ACCT_SUB_PAGES,"notifications","messages","documents","doc_builder","leases","inspections","vendors","moveout","evictions","housy"] },
  office_assistant: { label: "Office Assistant", color: "bg-info-500", pages: ["dashboard","tasks","properties","tenants","prospects","payments","maintenance","utilities","hoa","tax_bills","licenses","accounting",...ACCT_SUB_PAGES,"notifications","messages","admin","documents","doc_builder","leases","inspections","vendors","moveout","evictions","housy"] },
  accountant: { label: "Accountant", color: "bg-positive-600", pages: ["dashboard","accounting",...ACCT_SUB_PAGES,"payments","utilities"] },
  maintenance: { label: "Maintenance", color: "bg-notice-500", pages: ["maintenance","vendors"] },
  // Tenant sees the portal split into per-tab sidebar items, mirroring
  // the staff sidebar UX. Each tenant_* page routes to the same
  // TenantPortal component with a different initialTab so the nav
  // feels like a real app instead of an in-page tab strip.
  tenant: { label: "Tenant", color: "bg-brand-50/300", pages: ["tenant_portal", "tenant_overview", "tenant_pay", "tenant_autopay", "tenant_ledger", "tenant_maintenance", "tenant_documents", "tenant_messages"] },
  owner: { label: "Owner", color: "bg-highlight-600", pages: ["owner_portal","loans"] },
};

const ALL_NAV = [
  { id: "dashboard", label: "Dashboard", icon: "dashboard" },
  { id: "properties", label: "Properties", icon: "apartment", children: [
    { id: "maintenance", label: "Maintenance", icon: "build" },
    { id: "inspections", label: "Inspections", icon: "checklist" },
    { id: "utilities", label: "Utilities", icon: "bolt" },
    { id: "hoa", label: "HOA Payments", icon: "holiday_village" },
    { id: "loans", label: "Loans", icon: "account_balance_wallet" },
    { id: "insurance", label: "Insurance", icon: "verified_user" },
    { id: "tax_bills", label: "Tax Bills", icon: "receipt_long" },
    { id: "licenses", label: "Licenses", icon: "verified" },
  ]},
  { id: "tenants", label: "Tenants", icon: "people" },
  { id: "prospects", label: "Prospects", icon: "person_search" },
  { id: "payments", label: "Payments", icon: "payments" },
  // impliesChildren: holding "accounting" grants the acct_* sub-pages.
  // Properties deliberately does NOT carry this flag -- its children
  // include Loans, Insurance and Import from Excel, which the role
  // allowlists withhold from manager and office_assistant on purpose.
  { id: "accounting", label: "Accounting", icon: "account_balance", impliesChildren: true, children: [
    { id: "acct_opening",    label: "Opening Balances",  icon: "restart_alt" },
    { id: "acct_coa",        label: "Chart of Accounts", icon: "list_alt" },
    { id: "acct_journal",    label: "Journal Entries",   icon: "receipt_long" },
    { id: "acct_recurring",  label: "Recurring Entries", icon: "autorenew" },
    { id: "acct_bankimport", label: "Bank Transactions", icon: "credit_card" },
    { id: "acct_qbimport",   label: "Import from QuickBooks", icon: "cloud_upload" },
    { id: "acct_reconcile",  label: "Reconcile",         icon: "account_balance_wallet" },
    { id: "acct_classes",    label: "Class Tracking",    icon: "category" },
    { id: "acct_reports",    label: "Reports",           icon: "assessment" },
  ]},
  { id: "doc_builder", label: "Document Builder", icon: "description" },
  { id: "housy", label: HOUSY.menuLabel, icon: "auto_awesome" },
  { id: "vendors", label: "Vendors", icon: "engineering" },
  { id: "tasks", label: "Tasks & Approvals", icon: "assignment" },
  { id: "owners", label: "Owners", icon: "person" },
  { id: "messages", label: "Messages", icon: "forum" },
  { id: "notifications", label: "Notifications", icon: "notifications_active" },
];

// Tenant-specific sidebar nav. Each item drives the same TenantPortal
// component but with a different initialTab — same pattern Accounting
// uses for its sub-pages. Order matches what tenants reach for most:
// see balance/lease (Overview), pay rent, manage autopay, audit the
// ledger, request maintenance, view docs, message the landlord.
const TENANT_NAV = [
  { id: "tenant_overview", label: "Overview", icon: "home" },
  { id: "tenant_pay", label: "Pay Rent", icon: "credit_card" },
  { id: "tenant_autopay", label: "Autopay", icon: "autorenew" },
  { id: "tenant_ledger", label: "Ledger", icon: "receipt_long" },
  { id: "tenant_maintenance", label: "Maintenance", icon: "build" },
  { id: "tenant_documents", label: "Documents", icon: "folder" },
  { id: "tenant_messages", label: "Messages", icon: "forum" },
];

// Flat list of all nav IDs including children (for settings UI and allowedPages)
const ALL_NAV_FLAT = ALL_NAV.flatMap(n => n.children ? [n, ...n.children] : [n]);
// Child page IDs that live under a parent in sidebar
const NAV_CHILD_IDS = new Set(ALL_NAV.flatMap(n => (n.children || []).map(c => c.id)));

const pageComponents = {
  dashboard: Dashboard,
  tasks: TasksAndApprovals,
  properties: Properties,
  tenants: Tenants,
  prospects: Prospects,
  payments: Payments,
  maintenance: Maintenance,
  utilities: Utilities,
  accounting: Accounting,
  // Each Accounting sub-page routes to the same Accounting component
  // with a baked initialTab. The component watches the prop and
  // syncs activeTab on change so nav-clicks within the section don't
  // unmount/remount stale state. Sub-page IDs match the sidebar
  // children defined in ALL_NAV.
  acct_opening:    (p) => <Accounting {...p} initialTab="opening" />,
  acct_coa:        (p) => <Accounting {...p} initialTab="coa" />,
  acct_journal:    (p) => <Accounting {...p} initialTab="journal" />,
  acct_recurring:  (p) => <Accounting {...p} initialTab="recurring" />,
  acct_bankimport: (p) => <Accounting {...p} initialTab="bankimport" />,
  acct_qbimport:   (p) => <Accounting {...p} initialTab="qbimport" />,
  property_import:     (p) => <PropertyImport {...p} mode="edit" />,
  property_import_add: (p) => <PropertyImport {...p} mode="add" />,
  property_import_edit:(p) => <PropertyImport {...p} mode="edit" />,
  acct_reconcile:  (p) => <Accounting {...p} initialTab="reconcile" />,
  acct_classes:    (p) => <Accounting {...p} initialTab="classes" />,
  acct_reports:    (p) => <Accounting {...p} initialTab="reports" />,
  documents: Documents, // no sidebar entry, but accessible via "Upload Document" buttons in property/tenant views
  inspections: Inspections,
  hoa: HOAPayments,
  loans: Loans,
  insurance: InsuranceTracker,
  tax_bills: TaxBills,
  licenses: Licenses,
  admin: AdminPage,
  leases: LeaseManagement,
  // LateFees is the per-tenant late-fee application page. Rule DEFAULTS
  // live on the Admin Settings tab, but the per-tenant apply flow needs
  // its own route (it was silently dropped from this map during an
  // earlier refactor — the component itself is still live).
  latefees: LateFees,
  vendors: VendorManagement,
  owners: OwnerManagement,
  notifications: EmailNotifications,
  messages: Messages,
  moveout: MoveOutWizard,
  evictions: EvictionWorkflow,
  doc_builder: DocumentBuilder,
  housy: Housy,
  tenant_portal: TenantPortal,
  // Tenant sidebar pages all route to TenantPortal with a baked
  // initialTab. activeTab inside TenantPortal syncs from the prop on
  // mount + on prop changes (useEffect), so clicking a sidebar item
  // swaps the tab without unmounting the whole component.
  tenant_overview:    (p) => <TenantPortal {...p} initialTab="overview" />,
  tenant_pay:         (p) => <TenantPortal {...p} initialTab="pay" />,
  tenant_autopay:     (p) => <TenantPortal {...p} initialTab="autopay" />,
  tenant_ledger:      (p) => <TenantPortal {...p} initialTab="history" />,
  tenant_maintenance: (p) => <TenantPortal {...p} initialTab="maintenance" />,
  tenant_documents:   (p) => <TenantPortal {...p} initialTab="documents" />,
  tenant_messages:    (p) => <TenantPortal {...p} initialTab="messages" />,
  owner_portal: OwnerPortal,
};

function SetPasswordScreen({ currentUser, onComplete, showToast }) {
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  async function handleSet() {
    if (pw.length < 8) { showToast("Password must be at least 8 characters.", "error"); return; }
    if (pw !== pw2) { showToast("Passwords do not match.", "error"); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    if (error) { setSaving(false); pmError("PM-1009", { raw: error, context: "updateUser password set" }); return; }
    // Persist password_set_at so the auth router doesn't re-prompt on next
    // login. Upsert because tenants invited via magic link have a
    // company_members row but NO app_users row yet — UPDATE alone wouldn't
    // touch anything. We grab company_id from their first membership to
    // satisfy the (email, company_id) unique index.
    try {
      const email = currentUser?.email || "";
      const { data: mem } = await supabase.from("company_members")
        .select("company_id, role, user_name")
        .ilike("user_email", emailFilterValue(email))
        .limit(1)
        .maybeSingle();
      await supabase.from("app_users").upsert({
        email,
        company_id: mem?.company_id || null,
        role: mem?.role || "tenant",
        name: mem?.user_name || email.split("@")[0],
        password_set_at: new Date().toISOString(),
      }, { onConflict: "email,company_id" });
    } catch (_) { /* non-fatal — worst case the user is prompted once more */ }
    setSaving(false);
    showToast("Password set! You can now log in with email and password.", "success");
    onComplete();
  }
  return (
    <div className="min-h-dvh safe-y safe-x bg-gradient-to-br from-brand-50 to-white flex items-center justify-center p-4">
    <div className="bg-white rounded-xl border border-neutral-200 shadow-card p-8 w-full max-w-sm text-center">
    <span className="material-icons-outlined text-4xl text-brand-500 mb-2">lock</span>
    <h2 className="text-xl font-bold text-neutral-800 mb-1">Set Your Password</h2>
    <p className="text-sm text-neutral-400 mb-6">Welcome, {currentUser?.email}. Create a password so you can log in anytime.</p>
    <div className="space-y-3 text-left">
    <div><label className="text-xs font-medium text-neutral-600 mb-1 block">New Password</label><Input type="password" placeholder="Min 8 characters" value={pw} onChange={e => setPw(e.target.value)} /></div>
    <div><label className="text-xs font-medium text-neutral-600 mb-1 block">Confirm Password</label><Input type="password" placeholder="Re-enter password" value={pw2} onChange={e => setPw2(e.target.value)} onKeyDown={e => e.key === "Enter" && handleSet()} /></div>
    </div>
    <Btn variant="primary" className="w-full mt-5" onClick={handleSet} disabled={saving}>{saving ? "Setting..." : "Set Password & Continue"}</Btn>
    <TextLink tone="neutral" size="xs" underline={false} onClick={onComplete} className="mt-3 block mx-auto">Skip for now</TextLink>
    </div>
    </div>
  );
}

// Recovery flow: user clicked the password-reset link in their email.
// Supabase exchanged the recovery token for a short-lived session before
// we got here, so updateUser({ password }) will succeed. After it does,
// we sign them out and return to the login screen — that way the new
// password is verified by them signing in fresh, instead of silently
// landing inside the app on the back of the recovery session.
function ResetPasswordScreen({ currentUser, showToast }) {
  const [pw, setPw] = React.useState("");
  const [pw2, setPw2] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  async function handleReset() {
    if (pw.length < 8) { showToast("Password must be at least 8 characters.", "error"); return; }
    if (pw !== pw2) { showToast("Passwords do not match.", "error"); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pw });
    if (error) { setSaving(false); pmError("PM-1009", { raw: error, context: "password reset" }); return; }
    // SIGNED_OUT event fires from signOut and routes back to "landing"
    // via the existing onAuthStateChange handler.
    await supabase.auth.signOut();
    showToast("Password reset. Please sign in with your new password.", "success");
  }
  return (
    <div className="min-h-dvh safe-y safe-x bg-gradient-to-br from-brand-50 to-white flex items-center justify-center p-4">
    <div className="bg-white rounded-xl border border-neutral-200 shadow-card p-8 w-full max-w-sm text-center">
    <span className="material-icons-outlined text-4xl text-brand-500 mb-2">lock_reset</span>
    <h2 className="text-xl font-bold text-neutral-800 mb-1">Reset Your Password</h2>
    <p className="text-sm text-neutral-400 mb-6">Enter a new password for {currentUser?.email}.</p>
    <div className="space-y-3 text-left">
    <div><label className="text-xs font-medium text-neutral-600 mb-1 block">New Password</label><Input type="password" placeholder="Min 8 characters" value={pw} onChange={e => setPw(e.target.value)} /></div>
    <div><label className="text-xs font-medium text-neutral-600 mb-1 block">Confirm Password</label><Input type="password" placeholder="Re-enter password" value={pw2} onChange={e => setPw2(e.target.value)} onKeyDown={e => e.key === "Enter" && handleReset()} /></div>
    </div>
    <Btn variant="primary" className="w-full mt-5" onClick={handleReset} disabled={saving}>{saving ? "Resetting..." : "Reset Password & Sign In"}</Btn>
    </div>
    </div>
  );
}

function AppInner() {
  const [screen, setScreenRaw] = useState("loading");
  // The page is read off the path, here and again after auth resolves.
  // It used to need a deepLinkRef, because a /#messages push click had
  // its hash overwritten by every setScreen() on the way through login
  // and the landing page had to be stashed at mount and replayed. A path
  // survives the auth round trip on its own.
  const [page, setPageRaw] = useState(() => pageForPath(window.location.pathname) || "dashboard");

  // Read by the "?" handler, which is bound once and so cannot close over
  // a live `page` value.
  const pageRef = useRef(page);
  pageRef.current = page;

  const [pageAction, setPageAction] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // "?" leads with the group that applies to the page showing, so the
  // sheet isn't mostly keys that don't work where the user is.
  const [shortcuts, closeShortcuts] = useShortcutsHost(useCallback(() => {
    const p = pageRef.current;
    return p === "acct_bankimport" ? "review" : p === "acct_journal" ? "je" : null;
  }, []));
  // Cmd+K (Ctrl+K elsewhere) opens the command palette from anywhere.
  // Bound on the document rather than a container so it works no matter
  // what has focus, and skipped while typing into a field so it can't
  // steal a keystroke from a form.
  useEffect(() => {
    const onKey = (e) => {
      const k = (e.key || "").toLowerCase();
      if (k !== "k" || !(e.metaKey || e.ctrlKey) || e.altKey) return;
      const el = document.activeElement;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      // Cmd+K inside a text field is a legitimate app shortcut too, so
      // still open — but never when a modifier-less key would be eaten.
      e.preventDefault();
      setPaletteOpen(v => !v);
      if (typing && el.blur) el.blur();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // depth: how many pages deep this visit is, carried in the history entry
  // so the phone header knows whether a Back arrow has anywhere to go.
  function setPage(p, action) { setPageAction(action || null); setPageRaw(p); const depth = (window.history.state?.depth || 0) + 1; window.history.pushState({ page: p, screen: "app", depth }, "", pageUrl(p)); setNavDepth(depth); }
  // Sweep stale page-scoped params whenever the settled page does not own
  // them -- boot and Back/Forward do not go through setPage.
  useEffect(() => {
    const next = sweptUrl(page);
    if (next) window.history.replaceState(window.history.state, "", next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
  // A screen is not a place: "login" and "company_select" are states of
  // the auth flow, not somewhere to link to. Writing them as "#" + s left
  // "/accounting/reports#company_select" in the URL bar after a refresh.
  // The entry still carries the screen in its STATE, so Back through
  // login behaves as before; pushState with no url leaves the address.
  function setScreen(s) { setScreenRaw(s); screenRef.current = s; if (s !== "app") window.history.pushState({ screen: s }, ""); }
  // Mirror `screen` into a ref so the auth-state-change subscriber
  // (which closes over state values from its mount-time render) can
  // read the CURRENT screen when deciding whether to re-route. Without
  // this, a stale "loading" value lets Supabase's repeat SIGNED_IN
  // events (fired on tab-visibility restore) bounce the user back to
  // the company selector.
  const screenRef = useRef("loading");

  // Back / Forward. ONE handler: there were two bound to this event with
  // different fallbacks, which is a race waiting to happen. The entry
  // usually carries the page in its state; entries pushed before this
  // deploy, or by a link paste, carry nothing and the PATH says where the
  // entry was. Only when that says nothing either is Dashboard the answer
  // -- the old router jumped to it for every stateless entry.
  useEffect(() => {
  const onPop = (e) => {
    const fromPath = pageForPath(window.location.pathname);
    if (e.state?.page) setPageRaw(e.state.page);
    else if (fromPath) setPageRaw(fromPath);
    else if (screenRef.current === "app") setPageRaw("dashboard");
    if (e.state?.screen) setScreenRaw(e.state.screen);
    setNavDepth(e.state?.depth || 0);
  };
  window.addEventListener("popstate", onPop);
  return () => window.removeEventListener("popstate", onPop);
  }, []);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  // iPhone home-screen apps have no edge-swipe back (WebKit turns it off
  // in standalone mode), so the phone header shows a Back arrow whenever
  // this visit has a page to go back to (Sahil, 2026-10-06).
  const [navDepth, setNavDepth] = useState(() => window.history.state?.depth || 0);
  const [expandedNav, setExpandedNav] = useState(() => {
    const initial = new Set();
    // Auto-expand parents whose child is the current page OR when the
    // current page IS the parent itself — landing on the Accounting /
    // Properties dashboard should reveal their sub-pages in the sidebar
    // without the user having to find the chevron.
    ALL_NAV.forEach(n => {
      if (!n.children) return;
      if (n.id === page || n.children.some(c => c.id === page)) initial.add(n.id);
    });
    return initial;
  });
  // Keep parents auto-expanded as the user navigates between them.
  // Without this, expandedNav is set only at first mount; clicking
  // 'Accounting' from another module would render the parent active
  // but its children stay hidden behind the chevron until you click it.
  // The Set merge preserves any parent the user manually toggled open.
  useEffect(() => {
    setExpandedNav(prev => {
      const next = new Set(prev);
      ALL_NAV.forEach(n => {
        if (!n.children) return;
        if (n.id === page || n.children.some(c => c.id === page)) next.add(n.id);
      });
      return next;
    });
  }, [page]);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  // Unread inbound messages (sender_role='tenant', read_at IS NULL) for
  // the sidebar badge on the Messages nav item. Polled every 30s.
  const [unreadMessages, setUnreadMessages] = useState(0);
  // Count shown on the sidebar "Tasks & Approvals" nav item. Kept
  // approximate — adds pending counts across the three approval
  // tables plus in-progress/completed wizards (treated as 1 each
  // even though each can fan out to many individual skip tasks).
  // Refresh on focus + every 30s so the badge stays close to live
  // without hammering the DB.
  const [pendingTasksCount, setPendingTasksCount] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [confirmConfig, setConfirmConfig] = useState(null);
  const confirmResolveRef = useRef(null);
  // Ref on the main scroll container so PullToRefresh can attach its
  // touch listeners to the element the user actually scrolls.
  const mainScrollRef = useRef(null);

  // Changing pages returns the main scroll container to the top. The app
  // scrolls <main>, not the window, so without this a new page inherits the
  // previous page's scroll position and can open partway (or fully) down.
  useEffect(() => {
    if (mainScrollRef.current) mainScrollRef.current.scrollTop = 0;
  }, [page]);

  function showToast(message, type = "info", errorObj = null) {
  const id = ++_toastIdCounter;
  if (errorObj?.isError) {
    const duration = errorObj.severity === "critical" ? 10000 : errorObj.severity === "error" ? 6000 : 4000;
    setToasts(prev => [...prev, { id, type: errorObj.severity === "critical" ? "error" : errorObj.severity, isError: true, code: errorObj.code, message: errorObj.message, action: errorObj.action }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  } else {
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }
  }
  setShowToastGlobal(showToast);
  function removeToast(id) { setToasts(prev => prev.filter(t => t.id !== id)); }

  // safeLedgerInsert is now top-level (accessible from all components)

  // postAccountingTransaction is now a top-level function (accessible from all components)

  function showConfirm(config) {
  return new Promise(resolve => {
  // An open question is answered "no" before the next replaces it (FE-30).
  if (confirmResolveRef.current) { try { confirmResolveRef.current(false); } catch (_) { /* already settled */ } }
  confirmResolveRef.current = resolve;
  setConfirmConfig(typeof config === "string" ? { message: config } : config);
  });
  }
  // With fields on the question, the answers come back ({ key: value });
  // otherwise true.
  function handleConfirm(values) { const r = confirmResolveRef.current; confirmResolveRef.current = null; r?.(values && typeof values === "object" ? values : true); setConfirmConfig(null); }
  function handleCancel() { const r = confirmResolveRef.current; confirmResolveRef.current = null; r?.(false); setConfirmConfig(null); }
  const [currentUser, setCurrentUser] = useState(null);
  // The second sign-in step, an authenticator code (2026-10-23): set while a signed-in person still owes
  // it. While set, the gate renders INSTEAD of whatever screen is current,
  // so Back/Forward to an old company_select entry cannot step around it.
  const [mfaGate, setMfaGate] = useState(null);
  async function needsMfaGate(user, resumed) {
    const st = await mfaState(supabase, user).catch(e => ({ mode: "error", factors: [], error: String(e?.message || e) }));
    if (st.mode === "none") return false;
    setMfaGate({ user, resumed, state: st });
    return true;
  }
  // Invitations to other companies: the banner and the bell (2026-10-20).
  const myInv = useMyInvitations(currentUser);
  const [inviteBusy, setInviteBusy] = useState("");
  async function answerFromBanner(kind, inv) {
    setInviteBusy(kind + ":" + inv.company_id);
    const r = await answerInvitation(kind, { companyId: inv.company_id });
    setInviteBusy("");
    myInv.reload();
    if (!r.ok) { showToast(r.error, "error"); return; }
    if (kind === "decline") { showToast("You declined the invitation to " + inv.company_name + ".", "info"); return; }
    showToast("You joined " + inv.company_name + ".", "success");
    if (r.company) handleSelectCompany(r.company, r.role);
  }
  const [userRole, setUserRole] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [customAllowedPages, setCustomAllowedPages] = useState(null);
  // Company context
  const [activeCompany, setActiveCompany] = useState(null);
  const userCaps = useCapabilities(activeCompany?.id || null);
  const [companySettings, setCompanySettings] = useState({ ...COMPANY_DEFAULTS });

  const [companyRole, setCompanyRole] = useState("");
  const [roleLoaded, setRoleLoaded] = useState(false);
  const [missingRPCs, setMissingRPCs] = useState([]);

  // Capture ?company= param once at startup (before any replaceState clears it)
  const [urlCompanyParam] = useState(() => new URLSearchParams(window.location.search).get("company"));

  useEffect(() => {
  // The Supabase user object does NOT carry amr (authentication methods
  // reference) — that's JWT-payload only. Relying on user.amr returned
  // false for every magic-link user, so nobody ever saw the
  // set-password prompt. We now rely on app_users.password_set_at as the
  // single source of truth: set on first successful password setup, and
  // backfilled for legacy users so they're not re-prompted.
  async function needsPasswordSetup(user) {
    if (!user?.email) return false;
    try {
      // The prompt exists purely for magic-link invitees: they land with a
      // session but no password of their own, and we want them to set one.
      // That state = an `app_users` row exists (created by the invite flow)
      // AND password_set_at is NULL.
      //   - No rows at all → user was onboarded through a path that didn't
      //     touch app_users (e.g., company creator, admin sign-up). They
      //     signed in using whatever credentials they already had, so
      //     there's nothing to set up.
      //   - At least one row with password_set_at populated → done.
      //   - All rows have password_set_at = null → prompt.
      const { data: rows } = await supabase.from("app_users")
        .select("password_set_at")
        .ilike("email", emailFilterValue(user.email))
        .limit(10);
      if (!rows || rows.length === 0) return false;
      return !rows.some(r => r.password_set_at);
    } catch (_) {
      // Transient fetch failure — err on the side of NOT interrupting the
      // user's normal flow. The next session will prompt if needed.
      return false;
    }
  }
  // resumed=true means the session already existed when this page loaded
  // (a refresh or a return visit). false means credentials were just
  // entered, and a login must always land on the company selector.
  // Route ONCE per signed-in user per page load. getSession() and the
  // INITIAL_SESSION / SIGNED_IN events all arrive at boot; each used to pick
  // the company again and re-run the whole startup (audit FE-13).
  let routedUserId = null;
  async function routeSignedIn(user, resumed) {
    if (user?.id && routedUserId === user.id) { setCurrentUser(user); return; }
    routedUserId = user?.id || null;
    setCurrentUser(user);
    const mustPrompt = await needsPasswordSetup(user);
    if (mustPrompt) { setScreen("set_password"); return; }
    if (await needsMfaGate(user, resumed)) return;
    setScreen("company_select");
    autoSelectCompany(user, resumed);
  }
  // Recovery flow: Supabase's detectSessionInUrl runs at client init,
  // exchanges the recovery hash for a session, and clears the hash.
  // We can't rely on the hash being present here, so we depend on the
  // PASSWORD_RECOVERY event (below) — but as a defense-in-depth, also
  // check the hash in case a future supabase-js version changes order.
  const landedFromRecovery =
    window.location.hash.includes("type=recovery") ||
    window.location.hash.includes("access_token=");
  supabase.auth.getSession().then(({ data: { session } }) => {
  if (landedFromRecovery && session) {
    setCurrentUser(session.user);
    setScreen("reset_password");
    return;
  }
  if (session) { routeSignedIn(session.user, true); }
  else { setScreen("landing"); }
  });
  const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((_event, session) => {
  // PASSWORD_RECOVERY fires after the user clicks the email reset link
  // and Supabase exchanges the token for a short-lived session. Catch
  // it explicitly so the user lands on the reset screen instead of
  // falling through to SIGNED_IN routing (which would drop them inside
  // the app with no password-change prompt — the bug this fixes).
  if (_event === "PASSWORD_RECOVERY" && session) {
    setCurrentUser(session.user);
    setScreen("reset_password");
    return;
  }
  if (session) {
    // Only route on fresh sign-in events; TOKEN_REFRESHED / USER_UPDATED
    // shouldn't reset the screen the user is currently on.
    //
    // Supabase also fires SIGNED_IN again when a tab regains focus
    // after the session is re-fetched — without this guard that
    // bounces an already-signed-in user back through the company
    // selector, wiping any in-flight form state. Skip the re-route if
    // we're already inside the app or on a post-landing screen.
    const onEntryScreen = screenRef.current === "loading" || screenRef.current === "landing" || screenRef.current === "login";
    if ((_event === "SIGNED_IN" || _event === "INITIAL_SESSION") && onEntryScreen) {
      // INITIAL_SESSION is emitted while restoring a stored session at
      // boot; SIGNED_IN means credentials were just submitted.
      routeSignedIn(session.user, _event === "INITIAL_SESSION");
    } else {
      setCurrentUser(session.user);
    }
  } else {
  setCurrentUser(null);
  setUserRole(null);
  setActiveCompany(null);
  setMfaGate(null);
  setScreen("landing");
  routedUserId = null;
  }
  });
  return () => { if (authSub) authSub.unsubscribe(); };
  }, []);

  // Identify the reporter on every Sentry event. Without this, every
  // production error shows up anonymous and we can't tell which user
  // (or which company) triggered it — turning "who hit this?" from a
  // grep into a guessing game. setUser(null) on logout so the next
  // visitor doesn't inherit the previous session's identity.
  useEffect(() => {
    if (currentUser) {
      Sentry.setUser({ id: currentUser.id });   // no email to Sentry (OPS-23)
      Sentry.setTag("role", userRole || "unknown");
      Sentry.setTag("company_id", activeCompany?.id || "none");
      Sentry.setTag("company_name", activeCompany?.name || "none");
    } else {
      Sentry.setUser(null);
      Sentry.setTag("role", null);
      Sentry.setTag("company_id", null);
      Sentry.setTag("company_name", null);
    }
  }, [currentUser, userRole, activeCompany]);

  // Inactivity timeout — auto-logout after 30 minutes of no user interaction
  useEffect(() => {
    if (!currentUser) return;
    const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes
    let timer = setTimeout(handleIdleLogout, IDLE_TIMEOUT);
    function resetTimer() {
      clearTimeout(timer);
      timer = setTimeout(handleIdleLogout, IDLE_TIMEOUT);
    }
    function handleIdleLogout() {
      supabase.auth.signOut();
    }
    const events = ["mousedown", "keydown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, resetTimer, { passive: true }));
    return () => {
      clearTimeout(timer);
      events.forEach(e => window.removeEventListener(e, resetTimer));
    };
  }, [currentUser]);

  // Auto-select company ONLY for tenant/owner roles — everyone else sees the company selector
  async function autoSelectCompany(user, resumed) {
  if (!user?.email) return;
  // Prefer UID-based lookup (faster, not email-dependent), fall back to email
  let memberships;
  if (user.id) {
  const { data: uidResult } = await supabase.from("company_members").select("company_id, role, status").eq("auth_user_id", user.id).eq("status", "active");
  if (uidResult && uidResult.length > 0) { memberships = uidResult; }
  }
  if (!memberships) {
  const { data: emailResult } = await supabase.from("company_members").select("company_id, role, status").ilike("user_email", emailFilterValue(user.email)).eq("status", "active");
  memberships = emailResult;
  }
  if (!memberships || memberships.length === 0) { setScreen("company_select"); return; }
  // Check for ?company=UUID in URL — auto-select that company if user is a member
  const urlCompanyId = urlCompanyParam || new URLSearchParams(window.location.search).get("company");
  if (urlCompanyId) {
  let match = memberships.find(m => m.company_id === urlCompanyId);
  // If not found in cached memberships, try a direct query (handles newly created companies)
  if (!match) {
  const { data: directMem } = await supabase.from("company_members").select("company_id, role, status").eq("company_id", urlCompanyId).ilike("user_email", emailFilterValue(user.email)).eq("status", "active").maybeSingle();
  if (directMem) match = directMem;
  }
  if (match) {
  const { data: company } = await supabase.from("companies").select("*").eq("id", urlCompanyId).maybeSingle();
  // Keep the path: it is the page now. (Under the hash router this line
  // rebuilt the URL from pathname — just "/" — so "?company=<id>#accounting"
  // became "/" and every shared deep link landed on the dashboard.)
  if (company) {
  // ?company= STAYS in the URL. It used to be stripped once consumed, for
  // a tidier address bar, and that traded away three things:
  //
  //   * refresh returned you to the company picker, because which company
  //     you had open lived only in React state
  //   * a URL copied from the address bar carried no company, so a
  //     colleague opened it in whichever company they last used
  //   * a bookmark could not name a company
  //
  // It is safe to keep because it is SELECTION, not authentication. The
  // membership check above decides whether this user may open it, and
  // every query underneath is still bounded by RLS and
  // get_user_company_ids() -- editing the id by hand grants nothing. The
  // URL says what you are looking at; the server says whether you may.
  //
  // pageUrl() only strips params belonging to OTHER pages, so an unowned
  // param like this one already survives navigation.
  handleSelectCompany(company, match.role, user); return; }
  }
  }
  // Only tenants auto-select their company (skip selector)
  const tenantMembership = memberships.find(m => m.role === "tenant");
  if (tenantMembership) {
  const { data: company } = await supabase.from("companies").select("*").eq("id", tenantMembership.company_id).maybeSingle();
  if (company) { handleSelectCompany(company, tenantMembership.role, user); return; }
  }
  // Second-chance auto-select: if the user's last-visited company is
  // still an active membership, restore it on reload. Without this,
  // Ctrl-R on any module dumps the admin back to the Company Selector
  // and loses their current page — surprising for a browser refresh.
  // Only on a resumed session, and only for the account that saved it.
  // A bare string is a pre-fix value belonging to no known user: ignore
  // it, so the first login after this ships gets a clean selector.
  if (resumed) {
    try {
      const raw = localStorage.getItem("lastCompanyId");
      const saved = raw && raw.startsWith("{") ? JSON.parse(raw) : null;
      if (saved && saved.u && user.id && saved.u === user.id && saved.c) {
        const lastMatch = memberships.find(m => m.company_id === saved.c);
        if (lastMatch) {
          const { data: company } = await supabase.from("companies").select("*").eq("id", saved.c).maybeSingle();
          if (company) { handleSelectCompany(company, lastMatch.role, user); return; }
        }
      }
    } catch (_e) { /* unreadable or unparseable — fall through to selector */ }
  }
  // Default: show selector (first login, multi-company, or stale lastCompanyId)
  setScreen("company_select");
  }

  function handleSelectCompany(company, role, explicitUser = null) {
  // `explicitUser` lets callers invoked right after setCurrentUser pass the
  // fresh auth user directly — avoids the race where component state
  // hasn't propagated by the time we compute the initial userProfile.
  const userForProfile = explicitUser || currentUser;
  // Clear previous company's cached data (including global caches)
  setNotifications([]);
  setUnreadCount(0);
  setMissingRPCs([]);
  // Clear global caches to prevent cross-company data leaks
  Object.keys(_classIdCache).forEach(k => delete _classIdCache[k]);
  Object.keys(_acctIdCache).forEach(k => delete _acctIdCache[k]);
  Object.keys(_tenantArCache).forEach(k => delete _tenantArCache[k]);
  // Reset window-level backfill flags
  window._propClassesSynced = false;
  window._tenantArBackfilled = false;
  window._jeRenumbered = false;
  window._classIdBackfilled = false;
  setActiveErrorContext(company.id, currentUser?.email || "", role || "");
  setActiveCompany(company);
  // Put the company in the URL and keep it there, so the address bar always
  // names the books on screen. Switching company REPLACES rather than
  // pushes: a switch is a change of context, not a place to go Back to, and
  // pushing would make Back silently swap the company underneath whatever
  // page you returned to.
  try {
    const q = new URLSearchParams(window.location.search);
    if (q.get("company") !== company.id) {
      q.set("company", company.id);
      window.history.replaceState(
        { ...(window.history.state || {}) }, "",
        window.location.pathname + "?" + q.toString() + window.location.hash,
      );
    }
  } catch (_e) { /* a URL we cannot rewrite must not stop the switch */ }
  // Stamped with the user id. A bare company id is browser-global, so
  // signing in as a second account on the same device inherited the
  // first account's company and skipped the selector entirely.
  try { localStorage.setItem("lastCompanyId", JSON.stringify({ u: userForProfile?.id || "", c: company.id })); } catch (_e) { pmError("PM-8006", { raw: _e, context: "save lastCompanyId to localStorage", silent: true }); }
  checkRPCHealth(company.id).then(m => setMissingRPCs(m)).catch(() => {});
  loadCompanySettings(company.id).then(s => {
    setCompanySettings(s);
    // Transient DB failure → we're rendering defaults that don't match
    // the stored config. Warn the user so they don't unknowingly save
    // default values over their real settings.
    if (s?._loadError) showToast("Couldn't load your company settings (" + s._loadError + "). Don't save the Settings tab until this refreshes.", "warning");
  }).catch(() => {});
  loadInboxNotifications(company.id);
  registerPushNotifications();
  // Auto-run daily notification check (rent reminders, lease expiry)
  autoNotificationCheck(company.id);
  // Ensure default chart of accounts exists BEFORE any accounting
  // operations. Staff only: the role guard used to wrap just the
  // auto-posting below, so every tenant and owner login also attempted
  // to seed the chart of accounts. They have no write access to
  // acct_accounts, so that was 11 RLS-refused inserts and 11 silent
  // PM-4006 errors on every single portal load.
  if (role !== "tenant" && role !== "owner") {
  // Rent and due lease changes are NOT posted here any more: the server does
  // it every morning (api/daily-reminders?task=rent), and "Post what is due"
  // on the Recurring rent page runs the same engine on demand. Posting on
  // every login ran it several times at once and charged nothing in a month
  // nobody logged in (audit MONEY-19 / OPS-16).
  ensureDefaultAccounts(company.id).catch(e => pmError("PM-4006", { raw: e, context: "chart of accounts seed", silent: true }));
  }
  setCompanyRole(role);
  setUserRole(role);
  setRoleLoaded(true);
  setUserProfile({ name: userForProfile?.email?.split("@")[0] || "User", email: userForProfile?.email, role: role });
  fetchUserRoleForCompany(userForProfile, company.id); // async — role + real name update via setState after fetch
  setScreen("app");
  // NOTE (2026-09-12): boot ADOPTS the page with setPageRaw rather than
  // calling setPage. Under the old hash router, boot called setPage,
  // which PUSHED a history entry for the URL the browser had just
  // loaded -- duplicating it, so one Back press went from a page to
  // itself. Switching that push to a replace was tried and was worse:
  // with no entry of its own the app fell out of history entirely on
  // Back. Adopting the path, as below, is the version that is right.
  // Land on whatever the path names, ADOPTING it rather than navigating:
  // setPage() would rebuild the path from the page id, and a page that
  // addresses something below itself would lose it — an F5 on
  // /accounting/reports/balance-sheet has to come back on the Balance
  // Sheet, not the catalogue.
  const pathPage = pageForPath(window.location.pathname);
  if (pathPage) {
    setPageRaw(pathPage);
  } else {
    // The path names no page (a bare "/"). `page` was seeded from it at
    // mount and updated by any navigation since, so it is the right
    // answer; re-push so the URL names it too. Hard-coding "dashboard"
    // yanked the user off the page they had just landed on, because this
    // runs a SECOND time whenever getSession().then(routeSignedIn) and
    // the INITIAL_SESSION handler both reach autoSelectCompany.
    setPage(pageRef.current || "dashboard");
  }
  }

  async function fetchUserRoleForCompany(user, companyId) {
  if (!user?.email || !companyId) return;
  try {
  const { data } = await supabase.from("company_members").select("*").eq("company_id", companyId).ilike("user_email", emailFilterValue(user.email)).eq("status", "active").maybeSingle();
  // Backfill auth_user_id for UID-based lookups
  if (data && !data.auth_user_id && user.id) {
  const { error: uidErr } = await supabase.from("company_members").update({ auth_user_id: user.id }).eq("id", data.id);
  if (uidErr) pmError("PM-1009", { raw: uidErr, context: "auth_user_id backfill", silent: true });
  }
  if (data) {
  setUserRole(data.role);
  setCompanyRole(data.role);
  let displayName = data.user_name;
  // For tenants, prefer the name on the tenants table over company_members
  // — invites occasionally save user_name as the email prefix when the
  // inviter didn't type a display name.
  if (!displayName && data.role === "tenant") {
    try {
      const { tenant: trow } = await fetchMyTenant(companyId, user.email, "name");
      if (trow?.name) displayName = trow.name;
    } catch (_) { /* fall through to email prefix */ }
  }
  setUserProfile({ name: displayName || user.email.split("@")[0], email: user.email, role: data.role });
  if (data.custom_pages) {
  try { const parsed = JSON.parse(data.custom_pages); if (Array.isArray(parsed)) setCustomAllowedPages(parsed); } catch { setCustomAllowedPages(null); }
  } else {
  setCustomAllowedPages(null);
  }
  }
  setRoleLoaded(true);
  } catch { setRoleLoaded(true); /* still mark loaded so UI doesn't hang */ }
  }

  function addNotification(icon, message, options = {}) {
  const n = { id: shortId(), icon, message, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }), read: false };
  setNotifications(prev => [n, ...prev].slice(0, 50));
  setUnreadCount(prev => prev + 1);
  // Persist to DB for notification history
  if (activeCompany?.id) {
  supabase.from("notification_inbox").insert([{
  company_id: activeCompany.id,
  icon, message,
  recipient_email: options.recipient || userProfile?.email || "",
  notification_type: options.type || "general",
  read: false,
  }]).then(({ error }) => { if (error) pmError("PM-8006", { raw: error, context: "inbox write", silent: true }); });
  }
  }


  // Push Notification Registration. Strategy refined per Apple's
  // documented quirks (see WebKit Bugzilla 273063 + Apple's web push
  // docs):
  //   1. Don't auto-prompt for permission on every launch — that's
  //      what the Notifications page button is for. If the user
  //      previously granted, we respect existing state.
  //   2. On every launch, RECONCILE the browser's subscription with
  //      our DB. Three cases:
  //         (a) browser has a sub, DB matches → no-op
  //         (b) browser has a sub, DB has stale/different endpoint
  //             → upsert new endpoint to DB
  //         (c) browser has no sub but permission is granted → Apple
  //             quietly revoked it; resubscribe and update DB
  //   3. NEVER fire a silent self-test push — that's exactly what
  //      gets us revoked in the first place.
  async function registerPushNotifications() {
  try {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  const registration = await navigator.serviceWorker.register("/sw.js");
  if (typeof Notification === "undefined" || Notification.permission !== "granted") {
    // Permission was never granted (or was denied) — don't auto-
    // prompt. The Notifications page hero card has the explicit
    // "Allow notifications" button users tap on first run.
    return;
  }
  const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";
  if (!VAPID_PUBLIC_KEY) { pmError("PM-8006", { raw: { message: "VAPID key not configured" }, context: "push registration", silent: true }); return; }

  function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
  }

  let subscription = await registration.pushManager.getSubscription();

  // Reconcile against server. Three states drive different actions:
  //   (a) browser has sub + DB has matching sub → no-op
  //   (b) browser has sub + DB row missing → server-side admin
  //       deleted the row to force a refresh; the browser-cached
  //       endpoint may be the silently-revoked one. Unsubscribe
  //       and resubscribe to get a fresh APNS endpoint.
  //   (c) browser has sub + DB has different endpoint → just upsert
  //       (browser already rotated for some reason).
  //   (d) browser has no sub + permission granted → Apple revoked
  //       client-side; resubscribe.
  if (activeCompany?.id && currentUser?.email) {
    const { data: existingRow } = await supabase.from("push_subscriptions")
      .select("subscription, dead_marked_at").eq("company_id", activeCompany.id)
      .eq("user_email", currentUser.email).maybeSingle();
    const existingEndpoint = existingRow?.subscription?.endpoint;
    const dbHasRow = !!existingRow;
    const subAlreadyMarkedDead = !!existingRow?.dead_marked_at;

    const needFreshSubscribe = !subscription
      || (subscription && !dbHasRow)
      || subAlreadyMarkedDead;

    if (needFreshSubscribe) {
      // Force-rotate to a fresh APNS endpoint. unsubscribe() tells the
      // push service to forget the old endpoint; subscribe() then
      // mints a new one. Apple silent-revocation lives on the OLD
      // endpoint; the new one starts with a clean slate.
      if (subscription) {
        try { await subscription.unsubscribe(); } catch (_e) { /* harmless */ }
      }
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    if (subscription) {
      const subJson = JSON.parse(JSON.stringify(subscription));
      if (existingEndpoint !== subJson.endpoint || subAlreadyMarkedDead) {
        await supabase.from("push_subscriptions").upsert([{
          company_id: activeCompany.id,
          user_email: currentUser.email,
          subscription: subJson,
        }], { onConflict: "company_id,user_email" }).then(({ error }) => {
          if (error) pmError("PM-8006", { raw: error, context: "push subscription save", silent: true });
        });
        // Clear stale health flags so the new sub starts clean.
        await supabase.from("push_subscriptions")
          .update({ last_sw_received_at: null, last_dispatch_at: null, dead_marked_at: null })
          .eq("company_id", activeCompany.id)
          .eq("user_email", currentUser.email);
      }
    }
  }
  } catch (e) { pmError("PM-8006", { raw: e, context: "push registration", silent: true }); }
  }


  async function autoNotificationCheck(cid) {
  try {
  const lastCheck = sessionStorage.getItem("notifCheck_" + cid);
  const today = new Date().toDateString();
  if (lastCheck === today) return; // Already checked today
  sessionStorage.setItem("notifCheck_" + cid, today);

  // Check rent due reminders
  const { data: activeLeases } = await supabase.from("leases").select("id, tenant_id, tenant_name, property, rent_amount, payment_due_day, end_date")
  .eq("company_id", cid).eq("status", "active").limit(200);
  if (!activeLeases) return;

  const todayDate = new Date();
  let queued = 0;

  for (const lease of activeLeases) {
  // Rent due reminder (3 days before due date)
  // The due day within each month's real length (LIFE-12).
  const dueIn = (y, m) => new Date(y, m, Math.min(lease.payment_due_day || 1, new Date(y, m + 1, 0).getDate()));
  let nextDue = dueIn(todayDate.getFullYear(), todayDate.getMonth());
  if (nextDue < new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate())) nextDue = dueIn(todayDate.getFullYear(), todayDate.getMonth() + 1);
  const daysToDue = Math.round((nextDue - new Date(todayDate.getFullYear(), todayDate.getMonth(), todayDate.getDate())) / 86400000);

  // The tenant by id: a name lookup failed for same-name tenants (LIFE-12).
  const tenantOf = async () => lease.tenant_id ? (await supabase.from("tenants").select("email").eq("company_id", cid).eq("id", lease.tenant_id).is("archived_at", null).maybeSingle()).data : null;
  if (daysToDue <= 3 && daysToDue >= 0) {
  const tenant = await tenantOf();
  if (tenant?.email) {
  // Once per lease and month: the database refuses a second row with
  // this key (the old lookup read a JSON string and never matched, LIFE-2).
  const monthKey = nextDue.getFullYear() + "-" + String(nextDue.getMonth()+1).padStart(2,"0");
  await queueNotification("rent_due", tenant.email, { tenant: lease.tenant_name, amount: lease.rent_amount, date: fmtDate(nextDue), property: lease.property, month: monthKey, leaseId: lease.id }, cid, { dedupKey: `rent_due:lease:${lease.id}:${monthKey}` });
  queued++;
  }
  }

  // Lease expiry warning (60 days before), once per lease and end date
  if (lease.end_date) {
  const daysLeft = daysUntil(lease.end_date);
  if (daysLeft <= (companySettings.lease_expiry_warning_days || 60) && daysLeft > 0) {
  const tenant = await tenantOf();
  if (tenant?.email) {
  await queueNotification("lease_expiry", tenant.email, { tenant: lease.tenant_name, property: lease.property, date: lease.end_date, daysLeft, leaseId: lease.id }, cid, { dedupKey: `lease_expiry:lease:${lease.id}:${lease.end_date}` });
  queued++;
  }
  }
  }
  }

  // Auto notification check complete
  } catch (e) { pmError("PM-8006", { raw: e, context: "auto notification check", silent: true }); }
  }

  // Load persisted notifications on company select
  async function loadInboxNotifications(cid) {
  const { data } = await supabase.from("notification_inbox").select("*")
  .eq("company_id", cid)
  .or("recipient_email.ilike." + escapeFilterValue(currentUser?.email || "none") + ",recipient_email.is.null")
  .order("created_at", { ascending: false }).limit(50);
  if (data) {
  setNotifications(data.map(n => ({
  id: n.id, icon: n.icon, message: n.message,
  time: new Date(n.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
  read: n.read, dbId: n.id,
  date: fmtDate(n.created_at),
  })));
  setUnreadCount(data.filter(n => !n.read).length);
  }
  }

  async function handleLogout() {
  setMfaGate(null);
  await supabase.auth.signOut();
  clearMembershipCache();
  try { localStorage.removeItem("lastCompanyId"); } catch (_e) { pmError("PM-8006", { raw: _e, context: "remove lastCompanyId from localStorage", silent: true }); }
  _toastIdCounter = 0;
  setScreen("landing");
  setNotifications([]);
  setUnreadCount(0);
  setCurrentUser(null);
  setUserRole(null);
  setRoleLoaded(false);
  setCustomAllowedPages(null);
  setActiveCompany(null);
  }

  function switchCompany() {
  // Clear caches that are company-specific
  for (const key in _acctIdCache) delete _acctIdCache[key];
  if (typeof window !== "undefined") { delete window._propClassesSynced; }
  setActiveCompany(null);
  setCompanyRole("");
  setUserRole(null);
  setRoleLoaded(false);
  setCustomAllowedPages(null);
  setNotifications([]);
  setUnreadCount(0);
  setScreen("company_select");
  setPage("dashboard");
  }

  const [loginMode, setLoginMode] = useState("login");

  // Guard: never render app without a valid company — redirect to selector
  useEffect(() => {
  if (screen === "app" && !activeCompany?.id) {
  setScreen("company_select");
  }
  }, [screen, activeCompany]);

  // Poll unread message count for the sidebar badge. Only staff care
  // about this (tenants see messages directly in their portal tab), so
  // gate on role to skip the work for tenants/owners.
  useEffect(() => {
  if (!activeCompany?.id) { setUnreadMessages(0); return; }
  if (userRole === "tenant" || userRole === "owner") { setUnreadMessages(0); return; }
  let cancelled = false;
  async function poll() {
    const { count } = await supabase.from("messages")
      .select("id", { count: "exact", head: true })
      .eq("company_id", activeCompany.id)
      .is("read_at", null)
      .eq("sender_role", "tenant");
    if (!cancelled) setUnreadMessages(count || 0);
  }
  poll();
  const id = setInterval(poll, 30000);
  const onFocus = () => poll();
  window.addEventListener("focus", onFocus);
  return () => { cancelled = true; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [activeCompany?.id, userRole, page]);

  // The Tasks & Approvals badge: the SAME loader as the page (utils/openWork.js,
  // 2026-10-13), so the number and the list always agree. Approvals routed
  // to this viewer + records missing something (one per tenant / property) +
  // the lease clock. Tenant users don't see this nav item, so skip the work.
  useEffect(() => {
    if (!activeCompany?.id) { setPendingTasksCount(0); return; }
    if (userRole === "tenant") { setPendingTasksCount(0); return; }
    const myEmail = userProfile?.email || currentUser?.email || "";
    let cancelled = false;
    async function poll() {
      try {
        const badgePages = customAllowedPages || ROLES[userRole]?.pages || ROLES[companyRole]?.pages || [];
        const w = await loadOpenWork(supabase, { companyId: activeCompany.id, userRole, userEmail: myEmail, allowedPages: badgePages, pageAll: fetchAllPaged });
        if (!cancelled) setPendingTasksCount(w.counts.total);
      } catch (_e) { /* silent — next poll retries */ }
    }
    poll();
    const id = setInterval(poll, 30000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => { cancelled = true; clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [activeCompany?.id, userRole, page, userProfile?.email, currentUser?.email, customAllowedPages, companyRole]);

  // Before every screen, "loading" included: a resumed session waits here.
  if (mfaGate) return <><MfaGate user={mfaGate.user} initial={mfaGate.state} onDone={() => { const g = mfaGate; setMfaGate(null); setScreen("company_select"); autoSelectCompany(g.user, g.resumed); }} onSignOut={handleLogout} /><ToastContainer toasts={toasts} removeToast={removeToast} /><ConfirmModal config={confirmConfig} onConfirm={handleConfirm} onCancel={handleCancel} /></>;
  if (screen === "loading") return <><div className="flex items-center justify-center h-dvh safe-y bg-brand-50/30"><Spinner /></div><ToastContainer toasts={toasts} removeToast={removeToast} /><ConfirmModal config={confirmConfig} onConfirm={handleConfirm} onCancel={handleCancel} /></>;
  if (screen === "landing") return <><LandingPage onGetStarted={(mode) => { setLoginMode(mode); setScreen("login"); }} /><ToastContainer toasts={toasts} removeToast={removeToast} /><ConfirmModal config={confirmConfig} onConfirm={handleConfirm} onCancel={handleCancel} /></>;
  if (screen === "login") return <><LoginPage onLogin={() => {}} onBack={() => setScreen("landing")} initialMode={loginMode} /><ToastContainer toasts={toasts} removeToast={removeToast} /><ConfirmModal config={confirmConfig} onConfirm={handleConfirm} onCancel={handleCancel} /></>;
  if (screen === "set_password") return <><SetPasswordScreen currentUser={currentUser} onComplete={async () => { if (await needsMfaGate(currentUser, false)) return; setScreen("company_select"); autoSelectCompany(currentUser); }} showToast={showToast} /><ToastContainer toasts={toasts} removeToast={removeToast} /><ConfirmModal config={confirmConfig} onConfirm={handleConfirm} onCancel={handleCancel} /></>;
  if (screen === "reset_password") return <><ResetPasswordScreen currentUser={currentUser} showToast={showToast} /><ToastContainer toasts={toasts} removeToast={removeToast} /><ConfirmModal config={confirmConfig} onConfirm={handleConfirm} onCancel={handleCancel} /></>;
  if (screen === "company_select") return <><CompanySelector currentUser={currentUser} onSelectCompany={handleSelectCompany} onLogout={handleLogout} showToast={showToast} showConfirm={showConfirm} /><ToastContainer toasts={toasts} removeToast={removeToast} /><ConfirmModal config={confirmConfig} onConfirm={handleConfirm} onCancel={handleCancel} /></>;

  if (!activeCompany?.id || !roleLoaded) {
  return (
  <div className="flex items-center justify-center h-dvh safe-y bg-brand-50/30">
  <div className="text-center">
  <Spinner />
  <p className="text-sm text-neutral-400 mt-4">{!activeCompany?.id ? "Loading company..." : "Loading your access..."}</p>
  </div>
  </div>
  );
  }

  // Build nav based on confirmed role (roleLoaded is true at this point).
  // Tenants get a dedicated TENANT_NAV (Overview / Pay Rent / Autopay /
  // Ledger / Maintenance / Documents / Messages) instead of the staff
  // sidebar — same pattern, different list.
  // Pages whose data the admin's permissions withhold are not shown (3.1).
  const baseAllowedPages = customAllowedPages || ROLES[userRole]?.pages || ROLES[companyRole]?.pages || ["dashboard"];
  // Parent → children implicit access. If the user has the parent
  // ("accounting"), they implicitly have all child page IDs
  // (acct_opening, acct_coa, …) too. Without this, clicking a child
  // in the sidebar sets page to e.g. "acct_coa" but safePage filters
  // it out (falling back to the first allowed page) because the
  // child id isn't in the allowedPages list. Mirrors the sidebar
  // filter logic so what's shown matches what's reachable.
  const allowedPages = (() => {
    const set = new Set(baseAllowedPages);
    for (const n of ALL_NAV) {
      if (n.impliesChildren && n.children && set.has(n.id)) {
        for (const c of n.children) set.add(c.id);
      }
    }
    // Last, so an implied child the permissions withhold stays hidden.
    return pagesAllowedByCaps(Array.from(set), userCaps);
  })();
  const adminNav = userRole === "tenant"
    ? TENANT_NAV
    : ALL_NAV.filter(n => allowedPages.includes(n.id) || (n.children && n.children.some(c => allowedPages.includes(c.id)))).map(n => {
        if (!n.children) return n;
        // If the user has access to the parent (e.g. "accounting"),
        // grant access to all sub-pages by default. Some custom_pages
        // rows were saved before the sub-page list existed, so they
        // include only the parent — and without this fallback the
        // sidebar dropdown renders empty for those users.
        const parentAllowed = !!n.impliesChildren && allowedPages.includes(n.id);
        const filteredChildren = n.children.filter(c => (parentAllowed && pagesAllowedByCaps([c.id], userCaps).length > 0) || allowedPages.includes(c.id));
        return { ...n, children: filteredChildren };
      });

  // Owner-admins (created their own company) get full app access
  // Only force owner_portal for owners invited into a PM's company
  const effectiveRole = userRole || companyRole || "office_assistant";
  const safePage = allowedPages.includes(page) ? page : allowedPages[0];
  // Tenants land on tenant_overview by default (sidebar nav drives the
  // rest). Legacy "tenant_portal" page hash still resolves to the
  // overview tab. Owners (non-admin in this company) still go to
  // owner_portal as a single page for now.
  const effectivePage = effectiveRole === "tenant"
    ? (page && page.startsWith("tenant_") && page !== "tenant_portal" ? page : "tenant_overview")
    : (effectiveRole === "owner" && companyRole !== "admin") ? "owner_portal" : safePage;
  const Page = pageComponents[effectivePage] || Dashboard;

  return (
  <CompanyScope.Provider value={activeCompany?.id || null}>
  <div className="flex h-dvh safe-y safe-x bg-surface-muted font-body overflow-hidden">
  {/* Sidebar */}
  <div className={`${sidebarOpen ? "flex" : "hidden"} md:flex flex-col w-56 bg-white/80 backdrop-blur-md border-r border-brand-50 z-50 fixed md:relative h-full safe-y`}>
  <div className="px-5 py-4 border-b border-brand-50">
  <div className="flex items-center gap-2">
  <BrandMark size={32} className="shrink-0 drop-shadow-sm" />
  <span className="font-display font-extrabold text-lg tracking-tight text-brand-900">Housify</span>
  </div>
  {activeCompany && (
  <div className="flex items-center justify-between mt-2">
  <div className="flex items-center gap-1.5 min-w-0">
  <span className="w-5 h-5 rounded-lg bg-brand-100 flex items-center justify-center text-brand-700 text-xs font-bold shrink-0">{activeCompany.name[0]}</span>
  <span className="text-xs text-neutral-500 truncate font-medium">{activeCompany.name}</span>
  </div>
  {userRole !== "tenant" && <TextLink tone="neutral" size="xs" underline={false} onClick={() => { setSidebarOpen(false); switchCompany(); }}  title="Switch Company" aria-label="Switch Company" className="shrink-0 ml-1 min-w-[2rem] min-h-[2rem] inline-flex items-center justify-center"><span className="material-icons-outlined text-sm">swap_horiz</span></TextLink>}
  </div>
  )}
  </div>
  <nav className="flex-1 py-3 px-2 overflow-y-auto">
  {adminNav.map(n => {
  const childIds = (n.children || []).map(c => c.id);
  const isParentActive = page === n.id || childIds.includes(page);
  const isExpanded = expandedNav.has(n.id);
  return (
  <div key={n.id}>
  <div className={`flex items-center rounded-lg mb-0.5 transition-all ${isParentActive ? "bg-brand-50 text-brand-700 font-semibold" : "text-neutral-500 hover:bg-brand-50/50 hover:text-neutral-700"}`}>
  <button onClick={() => { setPage(n.id); setSidebarOpen(false); }}
  className="flex-1 flex items-center gap-3 px-3 py-2.5 text-sm text-left">
  <span className="material-icons-outlined text-lg">{n.icon}</span><span className="flex-1">{n.label}</span>
  {n.id === "messages" && unreadMessages > 0 && (
  <span className="bg-danger-500 text-white rounded-full text-2xs font-bold px-1.5 py-0.5 min-w-[18px] text-center">{unreadMessages > 9 ? "9+" : unreadMessages}</span>
  )}
  {n.id === "tasks" && pendingTasksCount > 0 && (
  <span className="bg-warn-500 text-white rounded-full text-2xs font-bold px-1.5 py-0.5 min-w-[18px] text-center">{pendingTasksCount > 999 ? "999+" : pendingTasksCount}</span>
  )}
  </button>
  {n.children && <button onClick={(e) => { e.stopPropagation(); setExpandedNav(s => { const next = new Set(s); if (next.has(n.id)) next.delete(n.id); else next.add(n.id); return next; }); }}
  className="px-2 py-2.5 text-neutral-400 hover:text-neutral-700">
  <span className={`material-icons-outlined text-sm transition-transform ${isExpanded ? "rotate-180" : ""}`}>expand_more</span>
  </button>}
  </div>
  {n.children && isExpanded && n.children.map(c => (
  <button key={c.id} onClick={() => { setPage(c.id); setSidebarOpen(false); }}
  className={`w-full flex items-center gap-3 pl-9 pr-3 py-2 text-xs text-left transition-all rounded-xl mb-0.5 ${page === c.id ? "bg-brand-50 text-brand-700 font-semibold" : "text-neutral-400 hover:bg-brand-50/50 hover:text-neutral-600"}`}>
  <span className="material-icons-outlined text-base">{c.icon}</span>{c.label}
  </button>
  ))}
  </div>
  );
  })}
  </nav>
  </div>

  {/* Main Content */}
  <div className="flex-1 flex flex-col min-w-0">
  <header className="bg-white/80 backdrop-blur-md border-b border-brand-50 px-4 py-3 flex items-center gap-3 relative z-40">
  {navDepth > 0 && <button className="md:hidden text-neutral-500 hover:text-neutral-700 transition-colors -ml-1" onClick={() => window.history.back()} aria-label="Back" title="Back"><span className="material-icons-outlined">arrow_back</span></button>}
  <button className="md:hidden text-neutral-400 hover:text-neutral-600 transition-colors" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Menu"><span className="material-icons-outlined">menu</span></button>
  {/* Active company name — more useful at a glance than the current
      page title (which is redundant with the selected sidebar item and
      the page's own H1). On small screens we truncate so long company
      names don't push the avatar + bell off-screen. Falls back to the
      page label if no company is active yet. */}
  {/* Company name, then the search box. The name keeps its place -- it is
      the thing you most need to be sure of before touching the books --
      but it no longer takes the whole bar, because the palette needs a
      visible home. On small screens the search collapses to its icon
      rather than squeezing the name out. */}
  {/* On a phone the name gives way first (it truncates), the search box
      second (down to its icon), and the avatar and bell never shrink: a
      long company name used to push the avatar under the search button. */}
  <div className="min-w-0 shrink text-sm font-semibold text-neutral-700 truncate max-w-[45%] md:max-w-none">
  {activeCompany?.name || (effectivePage || page).replace(/_/g, " ")}
  </div>
  <div className="flex-1 min-w-[44px] max-w-md">
  <SearchTrigger onOpen={() => setPaletteOpen(true)} hint={`${MOD}K`} />
  </div>
  {/* ml-auto, because the search is flex-1 capped at max-w-md: once it
      stops growing, everything after it packs left and the account
      controls end up stranded mid-bar with empty space to their right.
      They belong at the far edge, which is where a person looks for
      them. */}
  <div className="relative ml-auto shrink-0">
  <button onClick={() => setShowUserMenu(!showUserMenu)} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-brand-50 transition-colors ${showUserMenu ? "bg-brand-50" : ""}`}>
  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${ROLES[userRole]?.color || "bg-brand-600"}`}>{userProfile?.name?.[0]?.toUpperCase() || "U"}</div>
  <span className="hidden md:inline text-xs font-semibold text-neutral-700">{userProfile?.name || currentUser?.email?.split("@")[0] || "User"}</span>
  <span className="material-icons-outlined text-sm text-neutral-400">expand_more</span>
  </button>
  {showUserMenu && <>
  {/* Header dropdown must sit above page-level gradients + backdrop-blur
      cards that establish their own stacking context (e.g. the tenant
      portal's purple banner was partially eclipsing the menu at z-40). */}
  <div className="fixed inset-0 z-[90]" onClick={() => setShowUserMenu(false)} />
  <div className="absolute right-0 top-full mt-1 bg-white border border-neutral-200 rounded-xl shadow-pop py-1 w-48 z-[95]">
    <MenuItem onClick={() => { setShowUserMenu(false); setShowUserProfile(true); }} tone="neutral" icon="person">Profile</MenuItem>
    {userRole !== "tenant" && <MenuItem onClick={() => { setShowUserMenu(false); switchCompany(); }} tone="neutral" icon="swap_horiz">Switch Company</MenuItem>}
    {userRole !== "tenant" && <MenuItem onClick={() => { setShowUserMenu(false); setPage("admin"); }} tone="neutral" icon="settings">Settings</MenuItem>}
    <div className="border-t border-neutral-100 my-1" />
    <MenuItem onClick={() => { setShowUserMenu(false); handleLogout(); }} tone="danger" icon="logout">Logout</MenuItem>
  </div>
  </>}
  </div>
  <div className="relative shrink-0">
  <button onClick={() => {
  setShowNotifications(!showNotifications);
  // Mark all as read in DB
  if (!showNotifications && activeCompany?.id && unreadCount > 0) {
  supabase.from("notification_inbox").update({ read: true })
  .eq("company_id", activeCompany.id).eq("read", false)
  .ilike("recipient_email", currentUser?.email || "none")
  .then(() => {});
  setUnreadCount(0);
  setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }
  }} className="relative w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center text-brand-600 hover:bg-brand-100 transition-colors">
  <span className="material-icons-outlined">notifications</span>
  {(unreadCount > 0 || myInv.open.length > 0) && (
  <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-danger-500 rounded-full ring-2 ring-white"></span>
  )}
  </button>
  {showNotifications && (
  <div className="absolute right-0 top-12 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl border border-neutral-200 shadow-card z-50">
  <div className="px-4 py-3 border-b border-brand-50 flex justify-between items-center">
  <span className="font-display font-bold text-neutral-700 text-sm">Notifications</span>
  <div className="flex gap-2">
  <TextLink tone="brand" size="xs" onClick={() => { setPage("notifications"); setShowNotifications(false); }}>View All</TextLink>
  <TextLink tone="neutral" size="xs" underline={false} onClick={() => { setNotifications([]); setShowNotifications(false); }}>Clear</TextLink>
  </div>
  </div>
  <div className="max-h-72 overflow-y-auto">
  {myInv.open.map(i => (
  <div key={"inv-" + i.member_id} className="px-4 py-3 border-b border-brand-50/50 bg-info-50/60 flex items-start gap-2">
  <span className="text-lg">{"\u2709\uFE0F"}</span>
  <div className="flex-1 text-sm text-neutral-700">{i.company_name} invited you as {INVITE_ROLE_LABELS[i.role] || i.role} — accept or decline in the banner above.</div>
  </div>
  ))}
  {notifications.length === 0 && myInv.open.length === 0 ? (
  <div className="px-4 py-6 text-center text-neutral-400 text-sm">No notifications yet</div>
  ) : (
  notifications.map((n, idx) => (
  <div key={n.id || n.dbId || `notif_${idx}`} className={"px-4 py-3 border-b border-brand-50/50 hover:bg-brand-50/30 flex items-start gap-2 transition-colors " + (!n.read ? "bg-brand-50/40" : "")}>
  <span className="text-lg">{String(n.icon || "\u{1F4CC}")}</span>
  <div className="flex-1">
  <div className="text-sm text-neutral-700">{String(n.message || "")}</div>
  <div className="text-xs text-neutral-400 mt-0.5">{String(n.time || "")}{n.date ? " \u00B7 " + String(n.date) : ""}</div>
  </div>
  </div>
  ))
  )}
  </div>
  </div>
  )}
  </div>
  </header>

  {/* overscroll-behavior-y: contain stops iOS Safari's rubber-band at
      the scroll container's edge instead of propagating it up the flex
      column — without this, pulling down on the dashboard dragged the
      header with it while the fixed sidebar stayed put, making them
      look desynced. PullToRefresh re-adds the "slide down to reload"
      gesture that PWAs lose in standalone mode. */}
  {/* Files (court forms, statements, receipts) open here on phones and in
      the installed app, where a new tab has no way back. */}
  <FileViewer />
  <PullToRefresh
    scrollRef={mainScrollRef}
    onRefresh={() => { window.location.reload(); }}
    className="flex-1 flex flex-col min-h-0"
  >
  {/*
    Messages-style pages need a flex-fill chain (no main-level scroll,
    no main-level padding) so the thread inside can have a properly
    bounded height. Without this, the messages container has nowhere
    to size against, the thread is unbounded, the composer falls off
    the bottom, and PullToRefresh intercepts thread scroll because
    isInsideNestedScroll requires scrollHeight > clientHeight (which
    a thread of unbounded height never satisfies).
    Mobile-only: on desktop the original scroll-with-padding layout
    still works correctly because the desktop messages container has
    a literal h-[calc(100dvh-180px)].
  */}
  {(() => {
    const isMessagesLikePage = effectivePage === "messages" || effectivePage === "tenant_messages";
    const mainCls = isMessagesLikePage
      ? "flex-1 flex flex-col min-h-0 overflow-hidden md:overflow-y-auto md:overscroll-contain md:p-6"
      : "flex-1 overflow-y-auto overscroll-contain p-4 md:p-6";
    return (
  <main ref={mainScrollRef} className={mainCls}>
  {missingRPCs.length > 0 && userRole === "admin" && (
  <div className="bg-warn-50 border border-warn-200 rounded-xl px-4 py-3 mb-4">
  <div className="text-sm font-semibold text-warn-800">{"\u26A0\uFE0F"} Missing Database Functions</div>
  <div className="text-xs text-warn-600 mt-1">The following RPCs need to be deployed: {missingRPCs.join(", ")}. Some features may not work until these are installed.</div>
  </div>
  )}
  <InvitationBanner invites={myInv.open} busy={inviteBusy} onAnswer={answerFromBanner} />
  <InviteLinkModal currentUser={currentUser} showToast={showToast} onLogout={handleLogout} onDeclined={myInv.reload} onAccepted={(co, role) => { myInv.reload(); if (co) handleSelectCompany(co, role); }} />
  {userRole === "admin" && activeCompany && <PendingRequestsPanel companyId={activeCompany.id} addNotification={addNotification} />}
  {userRole === "admin" && activeCompany && <PendingPMAssignments companyId={activeCompany.id} addNotification={addNotification} showToast={showToast} />}
  <ShortcutsHelp open={!!shortcuts} onClose={closeShortcuts} scope={shortcuts?.scope} />
  <CommandPalette
  open={paletteOpen}
  onClose={() => setPaletteOpen(false)}
  nav={adminNav}
  currentPage={effectivePage}
  companyName={activeCompany?.name || ""}
  onNavigate={(id, action) => setPage(id, action)}
  onSwitchCompany={() => setScreen("company_select")}
  />
  {/* Opened from Tasks & Approvals: a way back that does not depend on
      finishing what was opened. Pages also return on their own when the
      record closes (returnFromTask). */}
  {pageAction?.returnTo?.page === "tasks" && effectivePage !== "tasks" && (
    <div className="px-4 pt-3 -mb-1">
      <button type="button" onClick={() => setPage("tasks", null)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800 hover:underline">
        <span className="material-icons-outlined text-sm">arrow_back</span> Back to Tasks &amp; Approvals
      </button>
    </div>
  )}
  <ErrorBoundary key={effectivePage + "-" + activeCompany.id}>
  <React.Suspense fallback={<div className="flex justify-center py-16"><Spinner /></div>}>
  <Page
  key={activeCompany.id}
  addNotification={addNotification}
  notifications={notifications}
  setPage={setPage}
  initialAction={pageAction}
  currentUser={currentUser}
  userRole={userRole}
  userProfile={userProfile}
  allowedPages={allowedPages}
  companyId={activeCompany.id}
  activeCompany={activeCompany}
  showToast={showToast}
  showConfirm={showConfirm}
  companySettings={companySettings}
  setCompanySettings={setCompanySettings}
  />
  </React.Suspense>
  </ErrorBoundary>
  </main>
    );
  })()}
  </PullToRefresh>
  </div>

  {/* Mobile Bottom Nav */}
  {/* Bottom nav removed — mobile navigation via sidebar hamburger menu */}

  {sidebarOpen && <div className="fixed inset-0 bg-black/20 z-40 md:hidden" onClick={() => setSidebarOpen(false)} />}
  {showNotifications && <div className="fixed inset-0 z-30" onClick={() => setShowNotifications(false)} />}
  {showUserProfile && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
  <div className="bg-white rounded-xl shadow-pop w-full max-w-md max-h-[90vh] overflow-y-auto">
  <UserProfile currentUser={currentUser} onBack={() => setShowUserProfile(false)} showToast={showToast} showConfirm={showConfirm} />
  </div>
  </div>
  )}
  <ToastContainer toasts={toasts} removeToast={removeToast} />
  {/* The authenticator-code prompt for bank actions and stored logins (2026-10-24). */}
  <StepUpHost />
  <ConfirmModal config={confirmConfig} onConfirm={handleConfirm} onCancel={handleCancel} />
  </div>
  </CompanyScope.Provider>
  );
}

export default function App() {
  // Intercept the public signing route before any auth bootstrapping so anon
  // signers never trigger company/role lookups or landing redirects.
  const path = typeof window !== "undefined" ? window.location.pathname : "";
  if (path.startsWith("/sign/")) {
    const token = path.slice("/sign/".length).split(/[?#]/)[0];
    return <ErrorBoundary><React.Suspense fallback={<Spinner />}><PublicSignPage token={token} /></React.Suspense></ErrorBoundary>;
  }
  // The same for a rental application link: an applicant has no account.
  if (path.startsWith("/apply/")) {
    const token = path.slice("/apply/".length).split(/[?#]/)[0];
    return <ErrorBoundary><React.Suspense fallback={<Spinner />}><PublicApplyPage token={token} /></React.Suspense></ErrorBoundary>;
  }
  // Rewrite a legacy "/#acct_reports" URL to "/accounting/reports" BEFORE
  // AppInner renders: it resolves its first page from the path in a
  // useState initialiser, which runs after this line. replaceState, so
  // the old link is corrected in place with no reload. Permanent, not
  // transitional — those links are in bookmarks, in sent emails and in
  // push payloads already delivered. After the /sign/ check on purpose:
  // a signing link carries no page hash and must never be routed.
  // An emailed invitation link: kept for after sign-in, the URL reset to "/".
  if (path.startsWith("/invite/")) captureInviteToken();
  if (typeof window !== "undefined") normalizeLegacyUrl();
  return <ErrorBoundary><AppInner /></ErrorBoundary>;
}
